import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const { Title, Paragraph } = Typography;

interface RegisterFormData {
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  password: string;
  password_confirm: string;
}

export const RegisterPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const onFinish = async (values: RegisterFormData) => {
    if (values.password !== values.password_confirm) {
      message.error('Passwords do not match!');
      return;
    }

    setLoading(true);
    try {
      const trimmedValues = { ...values, email: values.email.trim() };
      const result = await register(trimmedValues);
      if (result.success) {
        message.success(result.message || 'Registration successful! Please check your email to verify your account.');
        // After successful registration, redirect to the login page 
        // with a state message for the user.
        navigate('/login', { state: { message: 'Please check your email for a verification link before logging in.' } });
      } else {
        message.error(result.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      // This catch block might be redundant if the register function handles all errors,
      // but it's good for catching unexpected issues.
      message.error('An unexpected error occurred during registration.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '80vh',
      padding: '20px'
    }}>
      <Card style={{ width: '100%', maxWidth: '500px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2}>Create an Account</Title>
          <Paragraph>Join Gudu Portal today</Paragraph>
        </div>

        <Form
          name="register"
          onFinish={onFinish}
          layout="vertical"
          size="large"
          scrollToFirstError
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please input your email address!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input 
              prefix={<MailOutlined />} 
              placeholder="Enter your email address" 
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="first_name"
              label="First Name"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Please input your first name!' }]}
            >
              <Input placeholder="First name" />
            </Form.Item>

            <Form.Item
              name="last_name"
              label="Last Name"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Please input your last name!' }]}
            >
              <Input placeholder="Last name" />
            </Form.Item>
          </div>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please input your password!' },
              { min: 8, message: 'Password must be at least 8 characters!' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Create a password"
            />
          </Form.Item>

          <Form.Item
            name="password_confirm"
            label="Confirm Password"
            rules={[{ required: true, message: 'Please confirm your password!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm your password"
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              style={{ width: '100%' }}
            >
              Register
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center' }}>
          <Paragraph>
            Already have an account? <Link to="/login">Sign in here</Link>
          </Paragraph>
        </div>
      </Card>
    </div>
  );
}; 